'use client';

import { useId } from 'react';
import type { CSSProperties } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, ease, mockItem, sceneItem, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * Platformlar — kapsam beyanı.
 *
 * Koyu bloğun bittiği, beyaz alanın başladığı yer. Okuyucu hero'yu ve marka
 * şeridini geçmiş durumda ve tek bir şey soruyor: "benim reklam hesabım
 * destekleniyor mu?" Bu bölüm o soruyu tek bakışta kapatıyor. Sosyal kanıt
 * değil — üstteki şerit onu zaten yapıyor.
 *
 * ── Neden ikinci bir logo şeridi DEĞİL ────────────────────────────────────
 *
 * Hemen üstünde bir logo sırası var (.brands-rail). Aynı jesti tekrar etmek
 * iki bölümü birbirinin kopyası gibi gösterir ve aradaki asıl ayrımı siler:
 * yukarısı "bu markalar bizi kullanıyor", burası "bu platformları
 * destekliyoruz". Burada üç platform kendi alanına oturuyor, logo yardımcı
 * işaret; asıl bilgi metin — bağlandığında NE olduğu.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Panel kanal kanal devreye giriyor. Her hücrede üç vuruş var:
 *
 *   1. üst hat soldan sağa çekiliyor  (hattın kendisi — tween, bir çizim)
 *   2. baştaki kısa parça yanıyor     (durum — yay, bir anahtar kapanması)
 *   3. içerik odaklanıyor             (blur → net)
 *
 * İkinci vuruş bölümün asıl bilgisini taşıyor: iki kanal yanıyor, biri
 * yanmıyor. TikTok'un parçası kesikli ve yavaş çiziliyor — "hat henüz
 * çekilmedi" demek. Tempo farkı süs değil, DESIGN.md §4'teki "ağırlık anlam
 * taşır" kuralının bu bölümdeki karşılığı.
 *
 * Durum hiçbir yerde YALNIZCA renkle ya da yalnızca hareketle anlatılmıyor
 * (DESIGN.md §1): her hücrede yazılı bir etiket var ("Aktif" / "Yakında");
 * kesikli hat ve renksiz işaret onu destekliyor, tek başına taşımıyor.
 *
 * Sonsuz döngü yok, parallax yok — MotionConfig'in reducedMotion="user"
 * ayarı bu bölüm için yeterli, ayrıca useReducedMotion gerekmiyor.
 *
 * ── Marka işaretleri ──────────────────────────────────────────────────────
 *
 * Meta ve TikTok svgl.app'ten (TikTok'un `light` varyantı: beyaz zemine
 * basılıyor). svgl'de Google Ads YOK — yalnızca genel Google "G" işareti var,
 * o da başka bir ürünün kimliği. Google Ads için depoda zaten duran resmî
 * işaret kullanılıyor (/logos/google-ads.svg, hero'nun partner rozetiyle aynı
 * dosya). Bu yüzden aşağıdaki listede yol tek tek yazılı.
 *
 * `scale`: işaretlerin en/boy oranı 0.88 (TikTok) ile 1.50 (Meta) arasında.
 * Marka şeridindeki gibi ortak kutuya sığdırmak yerine ortak YÜKSEKLİK
 * kullanılıyor — bant orada 3.5–6.0'dı, burada dar ve üçü de bir "işaret",
 * yazılı logo değil. Kalan optik fark bu çarpanla kapatılıyor: Meta'nın ince
 * şeridi aynı yükseklikte geniş görünüyor, TikTok'un nota kafası dar kalıyor.
 * Değerler mürekkep alanı TAHMİNİDİR (sunucuda rasterleyici yok); şeritteki
 * ölçülmüş değerlerle karıştırma, görsel kontrolde düzeltilebilir.
 */
const PLATFORMS = [
  { key: 'meta', mark: '/logos/brands/meta.svg', w: 256, h: 171, scale: 0.93, live: true },
  { key: 'google', mark: '/logos/google-ads.svg', w: 26, h: 26, scale: 1, live: true },
  { key: 'tiktok', mark: '/logos/brands/tiktok.svg', w: 352, h: 399, scale: 1.12, live: false },
] as const;

/* Bölüm: başlık bloğu → ızgara → dipnot. */
const frameV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

/* Hücreler soldan sağa. 3 × 0.14 = 0.42 sn — son hücre beklenmiyor (§5). */
const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose } },
};

const cellV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

/* Hattın çizilmesi: sabit hızda süpürme. Yay burada yanlış olurdu — çizgi
   yerine oturan bir nesne değil, çekilen bir hat. */
const ruleV: Variants = {
  hidden: { scaleX: 0 },
  show: { scaleX: 1, transition: { duration: dur.slow, ease: ease.out } },
};

const edgeV: Variants = {
  hidden: { scaleY: 0 },
  show: { scaleY: 1, transition: { duration: dur.base, ease: ease.out } },
};

/* Yanan parça: anahtar kapanıyor, o yüzden yay (snap). */
const litLiveV: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  show: { scaleX: 1, opacity: 1, transition: springFade(spring.snap) },
};

/* Bekleyen parça aynı yolu ağır ağır çiziyor — henüz kapanmamış bir anahtar. */
const litSoonV: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  show: {
    scaleX: 1,
    opacity: 1,
    transition: { duration: dur.slow, ease: ease.out },
  },
};

const bodyV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

export function Platforms() {
  const t = useTranslations('platforms');
  const uid = useId();

  return (
    <section className="platforms" id="platforms" aria-labelledby={`${uid}-title`}>
      {/* Yan sütunlar basılmıyor — .grid-item-8fr `grid-column: 2` ile kendi
          yerleşiyor. Çerçevenin dikey hatları hero'nunkilerin ve SSS'inkilerin
          tam üstünde: beyaz alan sayfanın ızgarasına oturuyor. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="platforms-frame" variants={frameV} initial={false} animate="show">
            <motion.div className="platforms-head" variants={headV}>
              <motion.p className="platforms-eyebrow" variants={sceneItem}>
                {t('eyebrow')}
              </motion.p>

              {/* Gerçek h2 — görsele gömülü yazı yok; arama terimleri
                  ("Meta, Google ve TikTok reklamları tek panelde") burada. */}
              <motion.h2 className="platforms-title" id={`${uid}-title`} variants={sceneItem}>
                {t.rich('title', { hl })}
              </motion.h2>

              {/* Tek başına alıntılanabilir olgu cümlesi: kim, hangi
                  platformlar, ne yapıyor. Bağlamsız okunduğunda da doğru —
                  "bunu da yapıyoruz" gibi öncesine bağımlı kurulmuyor, ve
                  TikTok'un henüz açık olmadığını cümlenin kendisi söylüyor. */}
              <motion.p className="platforms-lead" variants={sceneItem}>
                {t('lead')}
              </motion.p>
            </motion.div>

            {/* Liste (ul/li), kart yığını değil: ekran okuyucu bölüme girer
                girmez "3 öğeli liste" duyuyor — bölümün cevapladığı soru tam
                olarak bu, "kaç ve hangi platform". */}
            <motion.ul className="platforms-grid" variants={gridV}>
              {PLATFORMS.map(({ key, mark, w, h, scale, live }) => (
                <motion.li
                  key={key}
                  className={`platforms-cell platforms-cell--${live ? 'live' : 'soon'}`}
                  variants={cellV}
                >
                  {/* Hatlar ayrı öğe: border animasyonlanamaz, transform
                      animasyonlanır (DESIGN.md §1). Üç hücrenin üst hattı yan
                      yana gelince panelin tepesinde tek çizgi oluyor. */}
                  <motion.span className="platforms-rule" variants={ruleV} aria-hidden="true" />
                  <motion.span className="platforms-edge" variants={edgeV} aria-hidden="true" />
                  <motion.span
                    className="platforms-lit"
                    variants={live ? litLiveV : litSoonV}
                    aria-hidden="true"
                  />

                  <motion.div className="platforms-body" variants={bodyV}>
                    <motion.div className="platforms-mark-row" variants={sceneItem}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        className="platforms-mark"
                        src={mark}
                        alt={t(`items.${key}.name`)}
                        width={w}
                        height={h}
                        loading="lazy"
                        decoding="async"
                        style={{ '--mark-scale': scale } as CSSProperties}
                      />
                      <span className="platforms-status">
                        {t(live ? 'status.live' : 'status.soon')}
                      </span>
                    </motion.div>

                    {/* Platform adları düz metin — logo değil bu okunuyor. */}
                    <motion.h3 className="platforms-name" variants={mockItem}>
                      {t(`items.${key}.name`)}
                    </motion.h3>
                    <motion.p className="platforms-scope" variants={mockItem}>
                      {t(`items.${key}.scope`)}
                    </motion.p>
                    <motion.p className="platforms-text" variants={mockItem}>
                      {t(`items.${key}.text`)}
                    </motion.p>
                  </motion.div>
                </motion.li>
              ))}
            </motion.ul>

            <motion.p className="platforms-note" variants={sceneItem}>
              {t('note')}
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
